// Bill service for handling bill-related API calls

export interface Bill {
  id: string;
  username: string;
  cells: number[];
  expiredAt: string;
  billAmount: number;
  status: 'pending' | 'completed' | 'failed' | 'expired';
  createdAt: string;
  updatedAt: string;
  qr?: string;
  expAt?: number;
}

export interface BillDetail {
  bill: Bill;
}

export interface BillStatusResponse {
  billId: string;
  status: 'pending' | 'completed' | 'failed' | 'expired';
}

export interface HistoryBillsResponse {
  success: boolean;
  bills?: Bill[];
  count?: number;
  message?: string;
}

class BillService {
  private baseURL = process.env.REACT_APP_API_URL || 'http://localhost:3042'; // Backend API URL

  async getHistoryBills(username: string): Promise<HistoryBillsResponse> {
    try {
      const token = this.getAccessToken();
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      
      // Add Authorization header if token exists
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${this.baseURL}/v1/game/history?username=${encodeURIComponent(username)}`, {
        method: 'GET',
        headers: headers,
        credentials: 'include', // To send cookies
      });

      const data = await response.json();
      
      if (data.status && data.status.success) {
        return {
          success: true,
          bills: data.data.bills || [],
          count: data.data.count || 0,
        };
      } else {
        return {
          success: false,
          message: data.status?.message || 'Không thể lấy lịch sử hóa đơn',
        };
      }
    } catch (error) {
      console.error('Get history bills error:', error);
      return {
        success: false,
        message: 'Có lỗi xảy ra khi lấy lịch sử hóa đơn',
      };
    }
  }

  async getBillById(billId: string): Promise<{ success: boolean; bill?: Bill; message?: string }> {
    try {
      const token = this.getAccessToken();
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${this.baseURL}/v1/game/bill/${billId}`, {
        method: 'GET',
        headers: headers,
        credentials: 'include',
      });

      const data = await response.json();
      
      if (data.status && data.status.success) {
        return {
          success: true,
          bill: data.data.bill,
        };
      } else {
        return {
          success: false,
          message: data.status?.message || 'Không thể lấy thông tin hóa đơn',
        };
      }
    } catch (error) {
      console.error('Get bill by ID error:', error);
      return {
        success: false,
        message: 'Có lỗi xảy ra khi lấy thông tin hóa đơn',
      };
    }
  }

  async getBillStatus(billId: string): Promise<{ success: boolean; status?: string; message?: string }> {
    try {
      const token = this.getAccessToken();
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${this.baseURL}/v1/game/bill/${billId}/status`, {
        method: 'GET',
        headers: headers,
        credentials: 'include',
      });

      const data = await response.json();
      
      if (data.status && data.status.success) {
        return {
          success: true,
          status: data.data.status,
        };
      } else {
        return {
          success: false,
          message: data.status?.message || 'Không thể lấy trạng thái hóa đơn',
        };
      }
    } catch (error) {
      console.error('Get bill status error:', error);
      return {
        success: false,
        message: 'Có lỗi xảy ra khi lấy trạng thái hóa đơn',
      };
    }
  }

  async verifyPayment(billId: string): Promise<{ success: boolean; message?: string }> {
    try {
      const token = this.getAccessToken();
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${this.baseURL}/v1/game/verify-payment/${billId}`, {
        method: 'POST',
        headers: headers,
        credentials: 'include',
      });

      const data = await response.json();
      
      if (data.status && data.status.success) {
        return {
          success: true,
          message: data.data?.message || 'Xác minh thanh toán thành công',
        };
      } else {
        return {
          success: false,
          message: data.status?.message || 'Xác minh thanh toán thất bại',
        };
      }
    } catch (error) {
      console.error('Verify payment error:', error);
      return {
        success: false,
        message: 'Có lỗi xảy ra khi xác minh thanh toán',
      };
    }
  }

  // Utility method to get access token from cookie
  private getAccessToken(): string | null {
    const nameEQ = 'accessToken=';
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
  }
}

export const billService = new BillService();
export default billService;

